import type { ProjectJob } from '../api/types';

function uniqueUrls(urls: string[]): string[] {
  return [
    ...new Set(
      urls.filter((url) => typeof url === 'string' && url.trim().length > 0),
    ),
  ];
}

function originalUrlsFromMeta(job: ProjectJob): string[] {
  const metaUrls = job.meta?.urls;
  if (!Array.isArray(metaUrls)) return [];
  return uniqueUrls(
    metaUrls.filter((url): url is string => typeof url === 'string'),
  );
}

/** URLهای اصلی جاب: اول meta.urls، وگرنه نتایج ذخیره‌شده */
export function urlsForJobRetry(job: ProjectJob): string[] {
  const fromMeta = originalUrlsFromMeta(job);
  if (fromMeta.length > 0) return fromMeta;

  return uniqueUrls((job.results ?? []).map((row) => row.url));
}

function failedUrlsFromResults(job: ProjectJob): string[] {
  return uniqueUrls(
    (job.results ?? [])
      .filter((row) => !row.success)
      .map((row) => row.url),
  );
}

/**
 * آدرس‌هایی که باید دوباره ارسال شوند: ناموفق‌ها + URLهایی که هنوز نتیجه ندارند
 * (مثلاً جاب به‌خاطر stall صف نیمه کاره مانده). آدرس‌های موفق همین جاب دوباره
 * ارسال نمی‌شوند. برای رد کردن موفق‌های جاب‌های بعدی از API `retry-urls` استفاده کنید.
 */
export function urlsForJobRetryPreferFailed(job: ProjectJob): string[] {
  const succeeded = new Set(
    uniqueUrls(
      (job.results ?? [])
        .filter((row) => row.success)
        .map((row) => row.url),
    ),
  );

  const original = urlsForJobRetry(job);
  if (original.length > 0) {
    return original.filter((url) => !succeeded.has(url));
  }

  return failedUrlsFromResults(job);
}

/** آیا دکمهٔ «تلاش مجدد» برای این جاب معنا دارد؟ */
export function canRetryFailedJob(job: ProjectJob): boolean {
  if (
    job.state !== 'failed' &&
    job.state !== 'partial' &&
    job.state !== 'cancelled'
  ) {
    return false;
  }
  if (urlsForJobRetryPreferFailed(job).length > 0) return true;
  if (job.failedCount > 0) return true;
  const processed = job.successCount + job.failedCount;
  return processed < job.total && job.total > 0;
}
